import React, { useEffect, useMemo } from 'react';
import { Currency, DemeterSymbolContractModel, UnitOfMeasure } from '../../../../Generated/Raven-Demeter';
import useUnitOfMeasureConversionApi from '../../../Apis/Hooks/useUnitOfMeasureConversionApiHook';
import Table from '../../../Components/Tables/Table/Table';
import TableWrapper from '../../../Components/Tables/TableWrapper/TableWrapper';
import useLanguage from '../../../Services/Language/useLanguageHook';
import styles from '../MarketIntelligenceReports.module.scss';
import { CurrencyConversionRates, MarketPriceSymbolContractCompositeModel } from '../MarketIntelligenceReportsDefinitions';
import { arbitrageTableHeaders, CaseCorrectedUnitOfMeasureConversionMap } from './MarketIntelligenceTableDefinitions';

const defaultMinimumTableWidth = 700;

interface IMarketIntelligenceArbitrageTableProps {
    symbolContracts: MarketPriceSymbolContractCompositeModel[];
    arbitrageSymbolContracts: DemeterSymbolContractModel[];
    currencyConversionRates: CurrencyConversionRates;
    loading: boolean;
    testId?: string;
}

const defaultUnitOfMeasure = UnitOfMeasure.MetricTon;

const MarketIntelligenceArbitrageTable: React.FC<IMarketIntelligenceArbitrageTableProps> = (props: IMarketIntelligenceArbitrageTableProps) => {
    // Application hooks.
    const [translations] = useLanguage();

    // Text hook.
    const titleString = useMemo(() => `${props.symbolContracts[0].commodityDisplayName} Arbitrage`, [props.symbolContracts]);

    // Api hooks.
    const [getUnitOfMeasureConversionRate, unitOfMeasureConversionMap] = useUnitOfMeasureConversionApi(props.symbolContracts[0]?.reutersInstrumentCodePrefix);

    const convertCurrency = (contract: MarketPriceSymbolContractCompositeModel, currency: Currency) => {
        if (!contract || !currency) {
            return 0;
        }

        const contractCurrency = contract.currency! as keyof CurrencyConversionRates;

        if (contract.currency === currency || !props.currencyConversionRates[contractCurrency]) {
            return contract.settlementPrice;
        }

        if (currency === Currency.Usd) {
            return contract.settlementPrice / props.currencyConversionRates[contractCurrency];
        }

        if (contract.currency !== Currency.Usd) {
            return (
                contract.settlementPrice /
                props.currencyConversionRates[contractCurrency] /
                props.currencyConversionRates[currency! as keyof CurrencyConversionRates]
            );
        }

        return contract.settlementPrice / props.currencyConversionRates[currency as keyof CurrencyConversionRates];
    };

    useEffect(() => {
        getUnitOfMeasureConversionRate(defaultUnitOfMeasure);
    }, []);

    const arbitrageTableRows = useMemo(() => {
        if (!props.arbitrageSymbolContracts) {
            return [];
        }

        return props.arbitrageSymbolContracts
            .filter((x) => props.symbolContracts.find((y) => y.month === x.month && y.year === x.year))
            .map((x) => {
                const unitOfMeasureConversion = (unitOfMeasureConversionMap as CaseCorrectedUnitOfMeasureConversionMap)?.metricTon;
                const currentSymbolContract = props.symbolContracts.find((y) => y.month === x.month && y.year === x.year);

                return {
                    reutersInstrumentCode: currentSymbolContract?.reutersInstrumentCode,
                    arbitrageName: `${currentSymbolContract?.monthYearName ?? ''} `,
                    dollar: convertCurrency(currentSymbolContract!, Currency.Usd) * unitOfMeasureConversion!,
                    euro: convertCurrency(currentSymbolContract!, Currency.Eur) * unitOfMeasureConversion!,
                    pound: convertCurrency(currentSymbolContract!, Currency.Gbp) * unitOfMeasureConversion!,
                };
            });
    }, [props.arbitrageSymbolContracts, props.symbolContracts, props.currencyConversionRates]);

    return (
        <div data-testid={props.testId} className={styles.market_intelligence_reports_table}>
            <TableWrapper title={titleString} isLoading={props.loading} track={{ eventCategory: 'MarketIntelligenceReports', eventAction: 'ListArbitrages' }}>
                <Table
                    tableHeaders={arbitrageTableHeaders}
                    tableData={arbitrageTableRows!}
                    tableStyle="alternatingRows"
                    minimumTableWidth={defaultMinimumTableWidth}
                />
            </TableWrapper>
        </div>
    );
};

export default MarketIntelligenceArbitrageTable;
